/** 用户主动停止或新语音替换旧语音时使用的内部错误。 */
export class AudioPlaybackStoppedError extends Error {
  /** 创建语音停止错误。 */
  constructor() {
    super("语音播放已停止");
    this.name = "AudioPlaybackStoppedError";
  }
}

/**
 * 向 WAV 数据视图写入 ASCII 标识。
 *
 * @param {DataView} view WAV 数据视图。
 * @param {number} offset 写入起始位置。
 * @param {string} text ASCII 文本。
 * @returns {void}
 */
function writeAscii(view, offset, text) {
  for (let index = 0; index < text.length; index += 1) view.setUint8(offset + index, text.charCodeAt(index));
}

/**
 * 生成一小段静音 WAV，用于在 iOS 用户手势内激活后续语音播放。
 *
 * @returns {Blob} 20 毫秒静音 WAV。
 */
function createSilentWavBlob() {
  const sampleRate = 8_000;
  const sampleCount = 160;
  const buffer = new ArrayBuffer(44 + sampleCount * 2);
  const view = new DataView(buffer);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + sampleCount * 2, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, sampleCount * 2, true);
  return new Blob([buffer], { type: "audio/wav" });
}

/** 复用同一个 HTMLAudioElement，避免 iOS 丢失首次用户播放授权。 */
export class AudioPlaybackController {
  /**
   * 创建语音播放控制器。
   *
   * @param {{createAudio?:Function, createObjectUrl?:Function, revokeObjectUrl?:Function}} options 浏览器能力或测试替身。
   */
  constructor(options = {}) {
    this.createAudio = options.createAudio || (() => new Audio());
    this.createObjectUrl = options.createObjectUrl || ((blob) => URL.createObjectURL(blob));
    this.revokeObjectUrl = options.revokeObjectUrl || ((url) => URL.revokeObjectURL(url));
    this.audio = null;
    this.activePlayback = null;
    this.primed = false;
  }

  /**
   * 获取并复用唯一的音频元素。
   *
   * @returns {HTMLAudioElement} 共享音频元素。
   */
  getAudio() {
    if (!this.audio) this.audio = this.createAudio();
    return this.audio;
  }

  /**
   * 在用户点击开始录音或开始练习时预激活 iOS 音频权限。
   *
   * @returns {Promise<void>} 激活尝试完成结果；失败时由正式播放提供手动兜底。
   */
  async prime() {
    if (this.primed || this.activePlayback) return;
    const audio = this.getAudio();
    const url = this.createObjectUrl(createSilentWavBlob());
    try {
      audio.src = url;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      this.primed = true;
    } catch {
      this.primed = false;
    } finally {
      audio.removeAttribute?.("src");
      audio.load?.();
      this.revokeObjectUrl(url);
    }
  }

  /**
   * 播放一段语音并等待播放完成，新语音会先停止旧语音。
   *
   * @param {Blob} blob 本地或网关返回的音频内容。
   * @returns {Promise<void>} 播放完成或失败结果。
   */
  play(blob) {
    this.stop();
    const audio = this.getAudio();
    const url = this.createObjectUrl(blob);
    audio.src = url;
    return new Promise((resolve, reject) => {
      const playback = {
        url,
        resolve,
        reject,
        onEnded: () => this.settle(playback),
        onError: () => this.settle(playback, new Error("语音播放失败")),
      };
      this.activePlayback = playback;
      audio.addEventListener("ended", playback.onEnded, { once: true });
      audio.addEventListener("error", playback.onError, { once: true });
      try {
        Promise.resolve(audio.play()).catch((error) => this.settle(playback, error));
      } catch (error) {
        this.settle(playback, error);
      }
    });
  }

  /**
   * 停止当前语音并让等待中的调用结束。
   *
   * @returns {void}
   */
  stop() {
    if (!this.activePlayback) return;
    this.audio.pause();
    this.audio.currentTime = 0;
    this.settle(this.activePlayback, new AudioPlaybackStoppedError());
  }

  /**
   * 清理一次播放的事件、对象 URL 和 Promise 状态。
   *
   * @param {object} playback 待结束的播放状态。
   * @param {Error|null} error 可选失败原因。
   * @returns {void}
   */
  settle(playback, error = null) {
    if (this.activePlayback !== playback) return;
    this.audio.removeEventListener("ended", playback.onEnded);
    this.audio.removeEventListener("error", playback.onError);
    this.audio.removeAttribute?.("src");
    this.audio.load?.();
    this.revokeObjectUrl(playback.url);
    this.activePlayback = null;
    if (error) playback.reject(error);
    else playback.resolve();
  }
}

const sharedAudioPlayback = new AudioPlaybackController();

/**
 * 在当前用户手势中预激活共享语音播放器。
 *
 * @returns {Promise<void>} 激活尝试完成结果。
 */
export function primeAudioPlayback() {
  return sharedAudioPlayback.prime();
}

/**
 * 使用共享播放器播放音频 Blob。
 *
 * @param {Blob} blob 音频内容。
 * @returns {Promise<void>} 播放完成或失败结果。
 */
export function playAudioBlob(blob) {
  return sharedAudioPlayback.play(blob);
}

/**
 * 停止共享播放器中的当前语音。
 *
 * @returns {void}
 */
export function stopAudioPlayback() {
  sharedAudioPlayback.stop();
}

/**
 * 判断错误是否源于主动停止语音，而非播放故障。
 *
 * @param {unknown} error 待判断错误。
 * @returns {boolean} 是否为主动停止错误。
 */
export function isAudioPlaybackStoppedError(error) {
  return error instanceof AudioPlaybackStoppedError;
}
